import { map, take } from 'rxjs'
import {
  mergeMap,
  switchMap,
  withLatestFrom,
  filter,
  catchError
} from 'rxjs/operators'
import { ofType } from 'redux-observable'
import Actions from '../actions'
import { catchAndDispatchError, getPayload } from '../utils/rxUtils'
import {
  serializeInitApplication,
  serializeSaveProgress,
  deserializerZipCodeRes
} from '../utils/serializerUtil'
import { STEPS } from 'consts/flowConsts'
import apis from '../apis'
import { of } from 'rxjs'
import { deserializeQualifyEvent } from 'utils/serializers/memberDeserializers'
import { DIALOG_TYPE } from 'consts'

export const initStep = action$ =>
  action$.pipe(
    getPayload(Actions.STEP_COMPLETED),
    filter(({ step }) => step === STEPS.ZIP_CODE),
    switchMap(() =>
      of(Actions.createAction(Actions.APPLICATION_INIT_REQUESTED))
    )
  )

export const continueStep = action$ =>
  action$.pipe(
    getPayload(Actions.STEP_COMPLETED),
    filter(({ step }) => step !== STEPS.ZIP_CODE),
    switchMap(({ step }) =>
      of(Actions.createAction(Actions.SAVE_PROGRESS_REQUESTED, { step }))
    )
  )

export const saveApplicationEpic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APPLICATION_INIT_REQUESTED),
    withLatestFrom(state$),
    map(([_, state]) => state),
    map(state => serializeInitApplication(state)),
    switchMap(serializedData => {
      return apis.initApplication(serializedData).pipe(
        mergeMap(response => {
          const { application_id, token, data } = response
          return [
            Actions.createAction(Actions.APPLICATION_INIT_RECEIVED, {
              applicationId: application_id,
              token: token,
              origin: 'application_init'
            }),
            Actions.createAction(Actions.STEP_SAVED, {
              step: STEPS.ZIP_CODE,
              data
            })
          ]
        }),
        catchAndDispatchError
      )
    })
  )

export const updateApplicationEpic = (action$, state$) =>
  action$.pipe(
    getPayload(
      Actions.SAVE_PROGRESS_REQUESTED,
      Actions.SHARE_APPLICATION_REQUESTED
    ),
    withLatestFrom(state$),
    map(serializeSaveProgress),
    switchMap(({ serializedData, step, skipNavigation = false, origin }) => {
      return apis.updateApplication(serializedData).pipe(
        mergeMap(({ data }) => {
          let actionsArray = []
          if (origin === 'share_application') {
            actionsArray.push(
              Actions.createAction(Actions.SHARE_APPLICATION_RECEIVED)
            )
          } else {
            actionsArray.push(
              Actions.createAction(Actions.SAVE_PROGRESS_RECEIVED)
            )
          }
          actionsArray.push(
            Actions.createAction(Actions.STEP_SAVED, {
              step,
              data,
              skipNavigation
            })
          )
          actionsArray.push(
            Actions.createAction(Actions.CHECK_IF_PLAN_PRICE_CHANGED, data)
          )
          return actionsArray
        }),
        catchAndDispatchError
      )
    })
  )

export const navNextStep = action$ =>
  action$.pipe(
    getPayload(Actions.STEP_SAVED),
    filter(({ skipNavigation }) => !skipNavigation),
    map(({ step }) =>
      Actions.createAction(Actions.STEP_FORWARD_NAVIGATED, { step })
    )
  )

export const validateZipCodeApi = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VALIDATE_ZIP_CODE_REQUESTED),
    withLatestFrom(state$),
    switchMap(([{ zipcode, memberId }, _]) => {
      return apis.validateZipcode(zipcode).pipe(
        map(res => {
          return Actions.createAction(Actions.VALIDATE_ZIP_CODE_RECEIVED, {
            data: deserializerZipCodeRes(res?.data),
            status: res?.status,
            memberId
          })
        }),
        catchAndDispatchError
      )
    })
  )

export const subscribeToStateWaitingList = action$ =>
  action$.pipe(
    getPayload(Actions.SUBSCRIBE_TO_STATE_WAITING_LIST_REQUESTED),
    switchMap(data => {
      return apis
        .subscribeToStateWaitingList({
          email: data?.email,
          zip_code: data?.zipCode
        })
        .pipe(
          map(() =>
            Actions.createAction(Actions.SUBSCRIBE_TO_WAITING_LIST_RECEIVED)
          ),
          catchAndDispatchError
        )
    })
  )

export const loadApplicationDataEpic = action$ =>
  action$.pipe(
    getPayload(Actions.APPLICATION_DATA_REQUESTED),
    switchMap(applicationId => {
      return apis.getApplicationData(applicationId).pipe(
        map(({ data }) =>
          Actions.createAction(Actions.APPLICATION_DATA_RECEIVED, { data })
        ),
        catchError(error => {
          if (error?.status === 403) {
            return of(Actions.createAction(Actions.UNAUTHORIZED_MODAL_OPENED))
          }
          throw error
        }),
        catchAndDispatchError
      )
    })
  )

export const getQualificationReasonsEpic = action$ =>
  action$.pipe(
    getPayload(Actions.QUALIFICATION_REASONS_REQUESTED),
    switchMap(payload => {
      return apis.getQualificationReasons(payload.locale).pipe(
        map(data => {
          return Actions.createAction(Actions.QUALIFICATION_REASONS_RECEIVED, {
            reasons: data
          })
        }),
        catchAndDispatchError
      )
    })
  )

export const loadQulifyLifeEventEpic = action$ =>
  action$.pipe(
    getPayload(Actions.APPLICATION_DATA_RECEIVED),
    map(deserializeQualifyEvent),
    map(data => Actions.createAction(Actions.QUALIFY_LIFE_EVENT_LOADED, data))
  )

export const subscribeToeEnrollmentReasonWaitingList = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SUBSCRIBE_TO_ENROLLMENT_REASON_WAITING_LIST_REQUESTED),
    withLatestFrom(state$),
    switchMap(([data, state]) => {
      return apis
        .subscribeToEnrollmentReasonWaitingList({
          email: data?.email,
          hashed_id: state.application?.applicationId
        })
        .pipe(
          map(() =>
            Actions.createAction(Actions.SUBSCRIBE_TO_WAITING_LIST_RECEIVED)
          ),
          catchAndDispatchError
        )
    })
  )

export const openSaveApplicationDialogEpic = action$ =>
  action$.pipe(
    getPayload(Actions.SAVE_DIALOG_OPEN_CLICKED),
    switchMap(({ type }) => {
      return action$.pipe(
        getPayload(Actions.APPLICATION_VALIDATED),
        take(1),
        filter(({ isValid }) => isValid),
        mergeMap(() => {
          let actions = [Actions.createAction(Actions.SAVE_DIALOG_OPENED)]
          if (type === DIALOG_TYPE.SHARE_APPLICATION) {
            actions.push(
              Actions.createAction(Actions.SHARE_APPLICATION_DIALOG_OPENED)
            )
          } else if (type === DIALOG_TYPE.SAVE_PROGRESS) {
            actions.push(
              Actions.createAction(Actions.SAVE_PROGRESS_DIALOG_OPENED)
            )
          }
          return actions
        })
      )
    })
  )
